import React, { useState } from 'react';
import { Heart, Star, Store, Gift, ArrowRight, Bell } from 'lucide-react';

// Demo restaurants (representative — Popular Near You) — using premium food photography
const DEMO_RESTAURANTS = [
  { id: 'luigis-pizza',     name: "Luigi's Pizza",     tags: ['Pizza', 'Italian'],   rating: 4.8, reviews: 320, time: '20–30 min', distance: '1.2 mi',
    img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&h=400&fit=crop&q=80' },
  { id: 'sushi-harbor',     name: 'Sushi Harbor',      tags: ['Sushi', 'Japanese'],  rating: 4.9, reviews: 256, time: '30–40 min', distance: '2.1 mi',
    img: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=600&h=400&fit=crop&q=80' },
  { id: 'burger-house',     name: 'Burger House',      tags: ['Burgers', 'American'],rating: 4.7, reviews: 185, time: '20–35 min', distance: '1.5 mi',
    img: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&h=400&fit=crop&q=80' },
  { id: 'casa-brasil-grill',name: 'Casa Brasil Grill', tags: ['Brazilian', 'BBQ'],   rating: 4.7, reviews: 231, time: '25–35 min', distance: '1.8 mi',
    img: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=600&h=400&fit=crop&q=80' },
];

const DiscoveryAndPopular = () => {
  const [liked, setLiked] = useState({});

  return (
    <section className="relative bg-black py-8 sm:py-12" data-testid="popular-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-6 lg:gap-8">
          {/* Popular Near You */}
          <div>
            <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Popular Near You
              </h2>
              <a
                href="/restaurants"
                className="text-sm font-bold text-gold-400 hover:text-gold-300 inline-flex items-center gap-1"
                data-testid="view-all-restaurants-link"
              >
                View all restaurants <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {DEMO_RESTAURANTS.map((r) => (
                <div
                  key={r.id}
                  data-testid={`restaurant-card-${r.id}`}
                  className="rounded-2xl bg-zinc-900 border border-zinc-800/70 overflow-hidden hover:border-gold-500/40 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(212,175,55,0.35)] ring-1 ring-white/[0.02]"
                >
                  <div className="relative h-40 overflow-hidden bg-zinc-950">
                    <img
                      src={r.img}
                      alt={r.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <button
                      onClick={() => setLiked((p) => ({ ...p, [r.id]: !p[r.id] }))}
                      className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/55 backdrop-blur flex items-center justify-center hover:bg-black/75 transition-colors"
                      aria-label="Favorite"
                      data-testid={`favorite-btn-${r.id}`}
                    >
                      <Heart className={`w-4 h-4 ${liked[r.id] ? 'fill-rose-500 text-rose-500' : 'text-white'}`} />
                    </button>
                  </div>
                  <div className="p-4 bg-zinc-900">
                    <div className="font-bold text-white truncate">{r.name}</div>
                    <div className="text-xs text-white/55 mt-0.5">{r.tags.join(' • ')}</div>
                    <div className="flex items-center gap-1.5 mt-2.5 text-xs">
                      <Star className="w-3.5 h-3.5 fill-gold-400 text-gold-400" />
                      <span className="text-white font-bold">{r.rating}</span>
                      <span className="text-white/45">({r.reviews})</span>
                      <span className="text-zinc-700 mx-1">·</span>
                      <span className="text-white/65">{r.time}</span>
                      <span className="text-zinc-700 mx-1">·</span>
                      <span className="text-white/65">{r.distance}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-white/55">Delivery · Pickup</div>
                    <a
                      href={`/${r.id}`}
                      className="mt-3 block text-center px-3 py-2.5 rounded-lg bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black text-sm font-bold transition-all"
                      data-testid={`view-menu-${r.id}`}
                    >
                      View Menu
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Not on NoFee yet? — viral growth card */}
          <aside
            className="rounded-2xl bg-gradient-to-br from-zinc-950 to-black border border-zinc-800 overflow-hidden flex flex-col"
            data-testid="not-on-nofee-card"
          >
            <div className="p-6 flex flex-col h-full">
              <div className="w-14 h-14 rounded-2xl bg-gold-500/10 border border-gold-500/30 flex items-center justify-center mb-4">
                <Store className="w-6 h-6 text-gold-400" />
              </div>
              <h3 className="text-lg font-extrabold text-white">Not on NoFee yet?</h3>
              <p className="mt-2 text-sm text-white/60 leading-snug">
                Bring your restaurant to NoFee and start saving.
              </p>
              <div className="mt-auto pt-5 space-y-2">
                <a
                  href="/customer#signup"
                  data-testid="request-restaurant-btn"
                  className="block text-center px-3 py-2.5 rounded-xl border border-gold-500/50 hover:border-gold-500 text-gold-300 hover:text-gold-200 text-sm font-bold inline-flex items-center justify-center gap-2 w-full transition-colors"
                >
                  <Gift className="w-4 h-4" /> Request a Restaurant
                </a>
                <a
                  href="/customer#signup"
                  data-testid="notify-me-btn"
                  className="block text-center px-3 py-2.5 rounded-xl text-white/65 hover:text-white text-sm font-medium inline-flex items-center justify-center gap-2 w-full transition-colors"
                >
                  <Bell className="w-4 h-4" /> Notify me
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default DiscoveryAndPopular;
